import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../../../../common/native/PrimaryButton";
import SecondaryButton from "../../../../common/native/SecondaryButton";
import {
  createIntegrationV2,
  getAllIntegrationSchemas,
  IntegrationCreateError,
  listIntegrationTypes,
  testIntegrationV2,
  TIntegrationSchemaDiscoveryResponse,
  TIntegrationTestPayloadV2,
} from "../../../../../services/api-integrations";
import { requestIntegrationAccess } from "../../../../../services/api-integrations-drchrono";
import { TSession } from "../../../../../utils/types";
import { buildCreateIntegrationPayload } from "../adapters/createIntegrationPayload";
import { buildIntegrationCatalog } from "../adapters/integrationCatalog";
import GuidedMappingEditor from "../mapping/GuidedMappingEditor";
import JsonMappingEditor from "../mapping/JsonMappingEditor";
import {
  buildDefaultMappingRows,
  buildPreviewPayload,
  ensureRequiredMappings,
  extractSourcePaths,
  makeId,
} from "../mapping/mappingUtils";
import PayloadPreview from "../mapping/PayloadPreview";
import SourcePathCatalogDrawer from "../mapping/SourcePathCatalogDrawer";
import {
  DEFAULT_SAMPLE_SUBMISSION,
  WEBHOOK_TEMPLATES,
} from "../templates/webhookTemplates";
import TOKENS from "../styles/tokens";
import {
  TFourDEMRSetup,
  TMetaPixelSetup,
  TPipedriveSetup,
  TValidationBadge,
  TWebhookMappingRow,
  TWebhookSetup,
} from "../types";
import {
  normalizeBackendValidationErrors,
  validateMappingRules,
  validateSchemaCompatibility,
} from "../validation/mappingValidation";
import IntegrationTypeSelector from "./IntegrationTypeSelector";

const DEFAULT_STEPS = [
  "Integration Type",
  "Auth / Destination",
  "Mapping",
  "Validate, Test, Save",
];

const META_PIXEL_STEPS = [
  "Integration",
  "Connection",
  "Events & Privacy",
  "Review",
];

const META_PIXEL_ID_PATTERN = /^\d{5,32}$/;
const META_SETUP_GUIDE_URL =
  "https://www.facebook.com/help/messenger-app/952192354843755";
const META_CONVERSIONS_API_OVERVIEW_URL =
  "https://www.facebook.com/business/help/2041148702652965";
const META_CONSENT_HANDOFF_SNIPPET = `const sendClinicOSMarketingConsent = (formIframe, granted) => {
  formIframe.contentWindow?.postMessage(
    {
      type: "clinicos:marketing-consent",
      granted,
    },
    new URL(formIframe.src).origin,
  );
};

// Call after the iframe loads and when consent changes:
sendClinicOSMarketingConsent(formIframe, true);`;

const makeDefaultWebhookState = (): TWebhookSetup => ({
  webhook_url: "",
  http_method: "POST",
  headers: [
    { id: makeId("header"), key: "Content-Type", value: "application/json" },
  ],
  mappings: buildDefaultMappingRows(),
  sample_payload_json: JSON.stringify(DEFAULT_SAMPLE_SUBMISSION, null, 2),
  template_id: null,
  json_mode_draft: JSON.stringify(
    {
      field_mappings: buildDefaultMappingRows().map((row) => ({
        target_field: row.target_field,
        source_path: row.source_path,
        default_value: row.default_value,
        literal_value: row.literal_value,
        include_if_none: row.include_if_none,
      })),
    },
    null,
    2,
  ),
});

const makeDefaultPipedriveState = (): TPipedriveSetup => ({
  client_id: "",
  client_secret: "",
  api_key: "",
  create_as: "lead",
  pipeline_id: "",
  stage_id: "",
  user_id: "",
});

const makeDefaultFourDEMRState = (): TFourDEMRSetup => ({
  client_id: "",
  client_secret: "",
  subscription_key: "",
  create_form_submissions_as: "lead",
  document_type_id: "",
  provider_ids_csv: "",
});

const makeDefaultMetaPixelState = (): TMetaPixelSetup => ({
  pixel_id: "",
  access_token: "",
  track_page_view: false,
});

const mappingsToJsonDraft = (mappings: TWebhookMappingRow[]): string =>
  JSON.stringify(
    {
      field_mappings: mappings.map((row) => ({
        target_field: row.target_field,
        source_path: row.source_path,
        default_value: row.default_value,
        literal_value: row.literal_value,
        include_if_none: row.include_if_none,
      })),
    },
    null,
    2,
  );

const parseJsonDraftMappings = (
  draft: string,
): { mappings?: TWebhookMappingRow[]; parseError: string | null } => {
  try {
    const parsed = JSON.parse(draft) as {
      field_mappings?: Array<Record<string, unknown>>;
    };

    if (!Array.isArray(parsed.field_mappings)) {
      return {
        parseError: "JSON must include a field_mappings array.",
      };
    }

    const mappings = parsed.field_mappings.map((item) => ({
      id: makeId("mapping"),
      target_field: String(item.target_field ?? ""),
      source_path: String(item.source_path ?? ""),
      default_value: String(item.default_value ?? ""),
      literal_value: String(item.literal_value ?? ""),
      include_if_none: Boolean(item.include_if_none),
    }));

    return {
      mappings,
      parseError: null,
    };
  } catch (error) {
    return {
      parseError: error instanceof Error ? error.message : "Invalid JSON.",
    };
  }
};

const readBackendTemplateConfig = (
  schema: TIntegrationSchemaDiscoveryResponse | undefined,
): Record<string, unknown> | null => {
  const template = schema?.template;
  if (!template || typeof template !== "object") return null;
  const config = (template as Record<string, unknown>).config;
  if (!config || typeof config !== "object") return null;
  return config as Record<string, unknown>;
};

const isSubmissionSamplePayload = (value: unknown): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;

  // Exclude create-integration request examples accidentally provided in schema examples.
  if (
    "config" in record ||
    "integration_type" in record ||
    "credentials" in record ||
    "is_active" in record
  ) {
    return false;
  }

  return (
    "patient_record" in record ||
    "form_data" in record ||
    "photo_urls" in record
  );
};

const toWebhookRowsFromBackendConfig = (
  config: Record<string, unknown> | null,
): TWebhookMappingRow[] => {
  if (!config) return buildDefaultMappingRows();

  const rawMappings = Array.isArray(config.field_mappings)
    ? (config.field_mappings as Array<Record<string, unknown>>)
    : [];

  const rows = rawMappings.map((item) => ({
    id: makeId("mapping"),
    target_field: String(item.target_field ?? ""),
    source_path: String(item.source_path ?? ""),
    default_value: String(item.default_value ?? ""),
    literal_value: String(item.literal_value ?? ""),
    include_if_none: Boolean(item.include_if_none),
  }));

  if (rows.length === 0) return buildDefaultMappingRows();
  return ensureRequiredMappings(rows);
};

type Props = {
  session: TSession;
  onIntegrationCreated?: () => void;
};

type TMetaVerificationStatus = "idle" | "checking" | "verified" | "unconfirmed";

const IntegrationWizard: React.FC<Props> = ({
  session,
  onIntegrationCreated,
}) => {
  const navigate = useNavigate();
  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth < 768;

  const [catalog, setCatalog] = useState<
    ReturnType<typeof buildIntegrationCatalog>
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [stepIndex, setStepIndex] = useState(0);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const [pipedrive, setPipedrive] = useState<TPipedriveSetup>(
    makeDefaultPipedriveState(),
  );
  const [fourDEMR, setFourDEMR] = useState<TFourDEMRSetup>(
    makeDefaultFourDEMRState(),
  );
  const [metaPixel, setMetaPixel] = useState<TMetaPixelSetup>(
    makeDefaultMetaPixelState(),
  );
  const [metaTouched, setMetaTouched] = useState({
    pixel_id: false,
    access_token: false,
  });
  const [showMetaAccessToken, setShowMetaAccessToken] = useState(false);
  const [showMetaConsentHandoff, setShowMetaConsentHandoff] = useState(false);
  const [metaVerificationStatus, setMetaVerificationStatus] =
    useState<TMetaVerificationStatus>("idle");
  const metaVerificationGenerationRef = useRef(0);
  const [webhook, setWebhook] = useState<TWebhookSetup>(
    makeDefaultWebhookState(),
  );
  const [mappingMode, setMappingMode] = useState<"guided" | "json">("guided");

  const [activeSourceMappingId, setActiveSourceMappingId] = useState<
    string | null
  >(null);

  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [requestAccessStatus, setRequestAccessStatus] = useState<string | null>(
    null,
  );

  const [hasValidated, setHasValidated] = useState(false);
  const [backendValidationErrors, setBackendValidationErrors] = useState<
    Array<{ path: string; message: string }>
  >([]);

  const invalidateMetaVerification = () => {
    metaVerificationGenerationRef.current += 1;
    setMetaVerificationStatus("idle");
  };

  useEffect(() => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [stepIndex]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setCatalogLoading(true);
        setCatalogError(null);

        const [metadata, schemaResponse] = await Promise.all([
          listIntegrationTypes(session),
          getAllIntegrationSchemas(session),
        ]);

        const rows = buildIntegrationCatalog(metadata, schemaResponse.schemas);
        if (!mounted) return;
        setCatalog(rows);

        if (!selectedType && rows.length > 0) {
          setSelectedType(rows[0].integration_type);
        }
      } catch (error) {
        if (!mounted) return;
        setCatalogError(
          error instanceof Error
            ? error.message
            : "Failed to load integration catalog.",
        );
      } finally {
        if (mounted) setCatalogLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [session]);

  const selectedIntegration = useMemo(
    () => catalog.find((row) => row.integration_type === selectedType) ?? null,
    [catalog, selectedType],
  );

  const selectedSchema = selectedIntegration?.schema;

  const isWebhook = selectedIntegration?.integration_type === "webhook";
  const isPipedrive = Boolean(
    selectedIntegration?.integration_type.startsWith("pipedrive_"),
  );
  const isFourDEMR = selectedIntegration?.integration_type === "4demr";
  const isMetaPixel =
    selectedIntegration?.integration_type.toLowerCase() === "meta_pixel";
  const stepTitles = isMetaPixel ? META_PIXEL_STEPS : DEFAULT_STEPS;

  const metaPixelIdError = useMemo(() => {
    const pixelId = metaPixel.pixel_id.trim();
    if (!pixelId) return "Enter your Meta Dataset ID.";
    if (!META_PIXEL_ID_PATTERN.test(pixelId)) {
      return "Use the 5 to 32 digit ID shown in Meta Events Manager.";
    }
    return null;
  }, [metaPixel.pixel_id]);

  const metaAccessTokenError = useMemo(
    () =>
      metaPixel.access_token.trim()
        ? null
        : "Enter a Conversions API access token.",
    [metaPixel.access_token],
  );

  useEffect(() => {
    if (!selectedIntegration) return;

    setHasValidated(false);
    setBackendValidationErrors([]);
    setSaveError(null);

    if (selectedIntegration.integration_type === "webhook") {
      const backendConfig = readBackendTemplateConfig(
        selectedIntegration.schema,
      );
      const seededMappings = toWebhookRowsFromBackendConfig(backendConfig);
      const seededHeaders =
        backendConfig &&
        backendConfig.headers &&
        typeof backendConfig.headers === "object"
          ? Object.entries(backendConfig.headers as Record<string, string>).map(
              ([key, value]) => ({
                id: makeId("header"),
                key,
                value,
              }),
            )
          : [
              {
                id: makeId("header"),
                key: "Content-Type",
                value: "application/json",
              },
            ];

      const examplePayload = selectedIntegration.schema?.examples?.[0]?.payload;
      const samplePayloadJson = isSubmissionSamplePayload(examplePayload)
        ? JSON.stringify(examplePayload, null, 2)
        : JSON.stringify(DEFAULT_SAMPLE_SUBMISSION, null, 2);

      setWebhook((prev) => ({
        ...prev,
        webhook_url:
          typeof backendConfig?.webhook_url === "string"
            ? backendConfig.webhook_url
            : prev.webhook_url,
        headers: seededHeaders,
        mappings: seededMappings,
        json_mode_draft: mappingsToJsonDraft(seededMappings),
        sample_payload_json: samplePayloadJson,
      }));
    }
  }, [selectedIntegration?.integration_type]);

  useEffect(() => {
    if (mappingMode === "guided") {
      setWebhook((prev) => ({
        ...prev,
        json_mode_draft: mappingsToJsonDraft(prev.mappings),
      }));
      return;
    }

    const parsed = parseJsonDraftMappings(webhook.json_mode_draft);
    if (!parsed.mappings || parsed.parseError) return;

    const nextMappings = ensureRequiredMappings(parsed.mappings);
    const currentSignature = JSON.stringify(
      webhook.mappings.map((row) => ({
        target_field: row.target_field,
        source_path: row.source_path,
        default_value: row.default_value,
        literal_value: row.literal_value,
        include_if_none: row.include_if_none,
      })),
    );

    const nextSignature = JSON.stringify(
      nextMappings.map((row) => ({
        target_field: row.target_field,
        source_path: row.source_path,
        default_value: row.default_value,
        literal_value: row.literal_value,
        include_if_none: row.include_if_none,
      })),
    );

    if (currentSignature !== nextSignature) {
      setWebhook((prev) => ({ ...prev, mappings: nextMappings }));
    }
  }, [mappingMode, webhook.json_mode_draft]);

  const parsedSamplePayload = useMemo(() => {
    try {
      return JSON.parse(webhook.sample_payload_json);
    } catch {
      return null;
    }
  }, [webhook.sample_payload_json]);

  const jsonParseResult = useMemo(
    () => parseJsonDraftMappings(webhook.json_mode_draft),
    [webhook.json_mode_draft],
  );

  const effectiveMappings = useMemo(() => {
    if (mappingMode !== "json") return webhook.mappings;
    if (jsonParseResult.parseError || !jsonParseResult.mappings)
      return webhook.mappings;
    return ensureRequiredMappings(jsonParseResult.mappings);
  }, [
    jsonParseResult.mappings,
    jsonParseResult.parseError,
    mappingMode,
    webhook.mappings,
  ]);

  const sourcePaths = useMemo(() => {
    const fromSample = parsedSamplePayload
      ? extractSourcePaths(parsedSamplePayload)
      : [];
    const fromMappings = effectiveMappings
      .map((row) => row.source_path.trim())
      .filter(Boolean);

    return Array.from(new Set([...fromSample, ...fromMappings])).sort();
  }, [effectiveMappings, parsedSamplePayload]);

  const previewPayload = useMemo(() => {
    if (!parsedSamplePayload) return null;
    return buildPreviewPayload(effectiveMappings, parsedSamplePayload);
  }, [effectiveMappings, parsedSamplePayload]);

  const mappingRuleValidation = useMemo(
    () => validateMappingRules(effectiveMappings),
    [effectiveMappings],
  );

  const payloadDraft = useMemo(
    () =>
      selectedIntegration
        ? buildCreateIntegrationPayload({
            selectedIntegration,
            pipedrive,
            fourDEMR,
            metaPixel,
            webhook,
          })
        : { errors: ["Choose an integration type first."] },
    [selectedIntegration, pipedrive, fourDEMR, metaPixel, webhook],
  );

  const schemaValidationErrors = useMemo(() => {
    if (!payloadDraft.payload || !selectedSchema) return [];
    return validateSchemaCompatibility(
      payloadDraft.payload,
      selectedSchema.create_schema,
    );
  }, [payloadDraft.payload, selectedSchema]);

  const badges: TValidationBadge[] = useMemo(() => {
    const parseOk = jsonParseResult.parseError == null;
    const schemaOk = schemaValidationErrors.length === 0;
    const rulesOk = mappingRuleValidation.errors.length === 0;

    if (isMetaPixel) {
      return [
        {
          label: "Dataset ID",
          status: metaPixelIdError ? "error" : "ok",
          detail: metaPixelIdError ?? "ID format is valid.",
        },
        {
          label: "Conversions API access token",
          status: metaAccessTokenError ? "error" : "ok",
          detail:
            metaAccessTokenError ??
            "Token provided. It will be stored as an encrypted credential.",
        },
        {
          label: "Backend schema",
          status: payloadDraft.payload ? (schemaOk ? "ok" : "error") : "idle",
          detail:
            !schemaOk && schemaValidationErrors.length
              ? schemaValidationErrors[0].message
              : undefined,
        },
      ];
    }

    return [
      {
        label: "Valid JSON",
        status: isWebhook ? (parseOk ? "ok" : "error") : "idle",
        detail:
          isWebhook && !parseOk
            ? (jsonParseResult.parseError ?? undefined)
            : undefined,
      },
      {
        label: "Schema Valid",
        status: payloadDraft.payload ? (schemaOk ? "ok" : "error") : "idle",
        detail:
          !schemaOk && schemaValidationErrors.length
            ? schemaValidationErrors[0].message
            : undefined,
      },
      {
        label: "Required Mappings Present",
        status: isWebhook ? (rulesOk ? "ok" : "error") : "idle",
        detail:
          !rulesOk && mappingRuleValidation.errors.length
            ? mappingRuleValidation.errors[0].message
            : undefined,
      },
    ];
  }, [
    isWebhook,
    isMetaPixel,
    jsonParseResult.parseError,
    mappingRuleValidation.errors,
    metaAccessTokenError,
    metaPixelIdError,
    payloadDraft.payload,
    schemaValidationErrors,
  ]);

  const errorsByPath = useMemo(() => {
    const map: Record<string, string[]> = {};
    if (!hasValidated) return map;

    [...mappingRuleValidation.errors, ...backendValidationErrors].forEach(
      (entry) => {
        if (!map[entry.path]) map[entry.path] = [];
        map[entry.path].push(entry.message);
      },
    );

    return map;
  }, [backendValidationErrors, hasValidated, mappingRuleValidation.errors]);

  const canAdvanceFromCurrentStep = useMemo(() => {
    if (stepIndex === 0) {
      return Boolean(selectedIntegration);
    }

    if (stepIndex === 1) {
      if (!selectedIntegration) return false;
      if (selectedIntegration.status === "coming_soon") return true;
      if (selectedIntegration.status === "existing_flow") return true;

      if (isWebhook) {
        return webhook.webhook_url.trim().length > 0;
      }

      if (isPipedrive) {
        if (selectedIntegration.integration_type === "pipedrive_api_key") {
          return pipedrive.api_key.trim().length > 0;
        }
        return (
          pipedrive.client_id.trim().length > 0 &&
          pipedrive.client_secret.trim().length > 0
        );
      }

      if (isFourDEMR) {
        return (
          fourDEMR.client_id.trim().length > 0 &&
          fourDEMR.client_secret.trim().length > 0 &&
          fourDEMR.subscription_key.trim().length > 0
        );
      }

      if (isMetaPixel) {
        return !metaPixelIdError && !metaAccessTokenError;
      }
    }

    if (stepIndex === 2) {
      if (!isWebhook) return true;

      if (mappingMode === "json" && jsonParseResult.parseError) {
        return false;
      }

      return mappingRuleValidation.errors.length === 0;
    }

    return true;
  }, [
    fourDEMR.client_id,
    fourDEMR.client_secret,
    fourDEMR.subscription_key,
    isFourDEMR,
    isMetaPixel,
    isPipedrive,
    isWebhook,
    jsonParseResult.parseError,
    mappingMode,
    mappingRuleValidation.errors.length,
    metaAccessTokenError,
    metaPixelIdError,
    pipedrive.api_key,
    pipedrive.client_id,
    pipedrive.client_secret,
    selectedIntegration,
    stepIndex,
    webhook.webhook_url,
  ]);

  const goNext = () => {
    if (isMetaPixel && stepIndex === 1) {
      setMetaTouched({ pixel_id: true, access_token: true });
    }
    if (!canAdvanceFromCurrentStep) return;
    setStepIndex((value) => Math.min(value + 1, stepTitles.length - 1));
  };

  const goBack = () => {
    setStepIndex((value) => Math.max(value - 1, 0));
  };

  const selectTemplate = (templateId: string) => {
    const template = WEBHOOK_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;

    const templateMappings = ensureRequiredMappings(
      template.mappings.map((row) => ({
        id: makeId("mapping"),
        target_field: row.target_field,
        source_path: row.source_path,
        default_value: row.default_value,
        literal_value: row.literal_value,
        include_if_none: row.include_if_none,
      })),
    );

    setWebhook((prev) => ({
      ...prev,
      template_id: template.id,
      webhook_url: template.webhook_url,
      headers: Object.entries(template.headers).map(([key, value]) => ({
        id: makeId("header"),
        key,
        value,
      })),
      mappings: templateMappings,
      json_mode_draft: mappingsToJsonDraft(templateMappings),
    }));
  };

  const updateWebhookHeader = (
    rowId: string,
    key: "key" | "value",
    value: string,
  ) => {
    setWebhook((prev) => ({
      ...prev,
      headers: prev.headers.map((row) =>
        row.id === rowId ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addWebhookHeader = () => {
    setWebhook((prev) => ({
      ...prev,
      headers: [...prev.headers, { id: makeId("header"), key: "", value: "" }],
    }));
  };

  const removeWebhookHeader = (rowId: string) => {
    setWebhook((prev) => ({
      ...prev,
      headers:
        prev.headers.length <= 1
          ? prev.headers
          : prev.headers.filter((row) => row.id !== rowId),
    }));
  };

  const requestAccess = async () => {
    if (!selectedIntegration) return;

    try {
      setRequestAccessStatus("Submitting request...");
      await requestIntegrationAccess(session, {
        integration_type: selectedIntegration.integration_type,
        source: "integrations_v2",
      });
      setRequestAccessStatus(
        "Custom integration request submitted. Our team will follow up soon.",
      );
    } catch (error) {
      setRequestAccessStatus(
        error instanceof Error
          ? error.message
          : "Failed to submit request access.",
      );
    }
  };

  const validateAndSimulate = async () => {
    setHasValidated(true);
    setSaveError(null);
    setSaveSuccess(null);

    if (isWebhook && mappingMode === "json" && jsonParseResult.parseError) {
      setSaveError(jsonParseResult.parseError);
      return;
    }

    if (isWebhook && mappingRuleValidation.errors.length > 0) {
      setSaveError(mappingRuleValidation.errors[0].message);
      return;
    }

    if (schemaValidationErrors.length > 0) {
      setSaveError(schemaValidationErrors[0].message);
      return;
    }

    if (!payloadDraft.payload) {
      setSaveError(
        payloadDraft.errors[0] ?? "Unable to validate configuration.",
      );
      return;
    }

    setSaveError(null);

    if (!isMetaPixel) return;

    const generation = metaVerificationGenerationRef.current + 1;
    metaVerificationGenerationRef.current = generation;
    setMetaVerificationStatus("checking");
    setIsValidating(true);

    try {
      const result = await testIntegrationV2(session, {
        integration_type: "meta_pixel",
        credentials: {
          access_token: metaPixel.access_token.trim(),
        },
        config: {
          pixel_id: metaPixel.pixel_id.trim(),
          track_page_view: metaPixel.track_page_view,
        },
      });

      if (metaVerificationGenerationRef.current !== generation) return;

      setMetaVerificationStatus(result.valid ? "verified" : "unconfirmed");
      setSaveError(
        result.valid
          ? null
          : result.message ||
              "Meta could not confirm this connection. Check the Dataset ID and access token, then try again.",
      );
    } catch (error) {
      if (metaVerificationGenerationRef.current !== generation) return;

      setMetaVerificationStatus("unconfirmed");
      setSaveError(
        error instanceof Error
          ? error.message
          : "Meta could not confirm this connection. Check the Dataset ID and access token, then try again.",
      );
    } finally {
      setIsValidating(false);
    }
  };

  const verifyMetaConnection = async (
    payload: TIntegrationTestPayloadV2,
    generation: number,
  ): Promise<void> => {
    let nextStatus: TMetaVerificationStatus = "unconfirmed";

    try {
      const result = await testIntegrationV2(session, payload);
      nextStatus = result.valid ? "verified" : "unconfirmed";
    } catch {
      nextStatus = "unconfirmed";
    }

    if (metaVerificationGenerationRef.current === generation) {
      setMetaVerificationStatus(nextStatus);
    }

    // TODO: Expand Meta verification with actionable failure messaging, retry
    // controls, and a product decision about when a failed check should block.
  };

  const handleSave = async () => {
    const metaVerificationGeneration = isMetaPixel
      ? metaVerificationGenerationRef.current + 1
      : null;

    if (metaVerificationGeneration !== null) {
      metaVerificationGenerationRef.current = metaVerificationGeneration;
    }

    setHasValidated(true);
    setSaveError(null);
    setSaveSuccess(null);
    setBackendValidationErrors([]);
    if (isMetaPixel) {
      setMetaVerificationStatus("idle");
    }

    if (!selectedIntegration) {
      setSaveError("Choose an integration type.");
      return;
    }

    if (selectedIntegration.status !== "ready") {
      setSaveError(
        "This integration type is not available for direct save in V2.",
      );
      return;
    }

    if (isWebhook && mappingMode === "json" && jsonParseResult.parseError) {
      setSaveError(jsonParseResult.parseError);
      return;
    }

    if (isWebhook && mappingRuleValidation.errors.length > 0) {
      setSaveError(mappingRuleValidation.errors[0].message);
      return;
    }

    if (!payloadDraft.payload) {
      setSaveError(payloadDraft.errors[0] ?? "Unable to build create payload.");
      return;
    }

    if (schemaValidationErrors.length > 0) {
      setSaveError(schemaValidationErrors[0].message);
      return;
    }

    try {
      setIsSaving(true);
      await createIntegrationV2(session, payloadDraft.payload);
      onIntegrationCreated?.();
      setSaveSuccess(
        isMetaPixel
          ? "Meta integration connected successfully."
          : "Integration saved successfully.",
      );
      setSaveError(null);
      setBackendValidationErrors([]);
      if (isMetaPixel && metaVerificationGeneration !== null) {
        if (
          metaVerificationGenerationRef.current === metaVerificationGeneration
        ) {
          setMetaVerificationStatus("checking");
        }
        void verifyMetaConnection(
          {
            integration_type: "meta_pixel",
            credentials: {
              access_token: metaPixel.access_token.trim(),
            },
            config: {
              pixel_id: metaPixel.pixel_id.trim(),
              track_page_view: metaPixel.track_page_view,
            },
          },
          metaVerificationGeneration,
        );
      }
      Alert.alert(
        isMetaPixel ? "Meta connected" : "Saved",
        isMetaPixel
          ? "Consented production form submissions can now send Lead events to Meta."
          : "Integration created successfully.",
      );
    } catch (error) {
      if (error instanceof IntegrationCreateError) {
        const normalized = normalizeBackendValidationErrors(error.details);
        setBackendValidationErrors(normalized);
        setSaveError(error.message || "Failed to create integration.");
      } else {
        setSaveError(
          error instanceof Error
            ? error.message
            : "Failed to create integration.",
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderStep1 = () => (
    <IntegrationTypeSelector
      catalog={catalog}
      selectedIntegrationType={selectedType}
      onSelect={(integrationType) => {
        setSelectedType(integrationType);
        setHasValidated(false);
        setSaveError(null);
        setSaveSuccess(null);
        invalidateMetaVerification();
      }}
    />
  );

  const renderPipedriveStep = () => {
    const needsApiKey =
      selectedIntegration?.integration_type === "pipedrive_api_key";

    return (
      <View style={styles.formSection}>
        <Text style={styles.sectionTitle}>Pipedrive Setup</Text>
        {needsApiKey ? (
          <>
            <Text style={styles.label}>API Key *</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.api_key}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, api_key: value }))
              }
              autoCapitalize="none"
            />
          </>
        ) : (
          <>
            <Text style={styles.label}>Client ID *</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.client_id}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, client_id: value }))
              }
              autoCapitalize="none"
            />
            <Text style={styles.label}>Client Secret *</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.client_secret}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, client_secret: value }))
              }
              autoCapitalize="none"
            />
          </>
        )}

        <Text style={styles.label}>Create As</Text>
        <View style={styles.rowButtons}>
          <Pressable
            style={[
              styles.segmentButton,
              pipedrive.create_as === "lead"
                ? styles.segmentButtonActive
                : null,
            ]}
            onPress={() =>
              setPipedrive((prev) => ({ ...prev, create_as: "lead" }))
            }
          >
            <Text style={styles.segmentButtonText}>Lead</Text>
          </Pressable>
          <Pressable
            style={[
              styles.segmentButton,
              pipedrive.create_as === "deal"
                ? styles.segmentButtonActive
                : null,
            ]}
            onPress={() =>
              setPipedrive((prev) => ({ ...prev, create_as: "deal" }))
            }
          >
            <Text style={styles.segmentButtonText}>Deal</Text>
          </Pressable>
        </View>

        {pipedrive.create_as === "deal" ? (
          <>
            <Text style={styles.label}>Pipeline ID (optional)</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.pipeline_id}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, pipeline_id: value }))
              }
            />
            <Text style={styles.label}>Stage ID (optional)</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.stage_id}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, stage_id: value }))
              }
            />
            <Text style={styles.label}>User ID (optional)</Text>
            <TextInput
              style={styles.input}
              value={pipedrive.user_id}
              onChangeText={(value) =>
                setPipedrive((prev) => ({ ...prev, user_id: value }))
              }
            />
          </>
        ) : null}
      </View>
    );
  };

  const renderWebhookStep = () => (
    <View style={styles.formSection}>
      <Text style={styles.sectionTitle}>Webhook Destination</Text>

      <View style={styles.templateContainer}>
        {WEBHOOK_TEMPLATES.map((template) => {
          const active = webhook.template_id === template.id;
          return (
            <Pressable
              key={template.id}
              style={[
                styles.templateCard,
                active ? styles.templateCardActive : null,
              ]}
              onPress={() => selectTemplate(template.id)}
            >
              <Text style={styles.templateTitle}>{template.name}</Text>
              <Text style={styles.templateDescription}>
                {template.description}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <Text style={styles.label}>Destination URL *</Text>
      <TextInput
        style={styles.input}
        placeholder="https://example.com/webhook"
        value={webhook.webhook_url}
        autoCapitalize="none"
        onChangeText={(value) =>
          setWebhook((prev) => ({ ...prev, webhook_url: value }))
        }
      />

      <Text style={styles.label}>HTTP Method</Text>
      <TextInput
        style={[styles.input, styles.disabledInput]}
        value={webhook.http_method}
        editable={false}
      />

      <Text style={styles.label}>Headers (optional)</Text>
      {webhook.headers.map((row) => (
        <View style={styles.inlineInputRow} key={row.id}>
          <TextInput
            style={[styles.input, styles.inlineInput]}
            value={row.key}
            placeholder="Header Name"
            autoCapitalize="none"
            onChangeText={(value) => updateWebhookHeader(row.id, "key", value)}
          />
          <TextInput
            style={[styles.input, styles.inlineInput]}
            value={row.value}
            placeholder="Header Value"
            autoCapitalize="none"
            onChangeText={(value) =>
              updateWebhookHeader(row.id, "value", value)
            }
          />
          <Pressable
            style={styles.smallActionButton}
            onPress={() => removeWebhookHeader(row.id)}
          >
            <Text style={styles.smallActionText}>Remove</Text>
          </Pressable>
        </View>
      ))}
      <SecondaryButton text="Add Header" onPress={addWebhookHeader} fullWidth />
    </View>
  );

  const renderFourDEMRStep = () => (
    <View style={styles.formSection}>
      <Text style={styles.sectionTitle}>4D EMR Setup</Text>
      <Text style={styles.helperText}>
        Required fields are marked with an asterisk.
      </Text>

      <Text style={styles.label}>Client ID *</Text>
      <TextInput
        style={styles.input}
        value={fourDEMR.client_id}
        onChangeText={(value) =>
          setFourDEMR((prev) => ({ ...prev, client_id: value }))
        }
        autoCapitalize="none"
      />

      <Text style={styles.label}>Client Secret *</Text>
      <TextInput
        style={styles.input}
        value={fourDEMR.client_secret}
        onChangeText={(value) =>
          setFourDEMR((prev) => ({ ...prev, client_secret: value }))
        }
        autoCapitalize="none"
      />

      <Text style={styles.label}>Subscription Key *</Text>
      <TextInput
        style={styles.input}
        value={fourDEMR.subscription_key}
        onChangeText={(value) =>
          setFourDEMR((prev) => ({ ...prev, subscription_key: value }))
        }
        autoCapitalize="none"
      />

      <Text style={styles.label}>Create Form Submissions As</Text>
      <View style={styles.rowButtons}>
        <Pressable
          style={[
            styles.segmentButton,
            fourDEMR.create_form_submissions_as === "lead"
              ? styles.segmentButtonActive
              : null,
          ]}
          onPress={() =>
            setFourDEMR((prev) => ({
              ...prev,
              create_form_submissions_as: "lead",
            }))
          }
        >
          <Text style={styles.segmentButtonText}>Lead</Text>
        </Pressable>
        <Pressable
          style={[
            styles.segmentButton,
            fourDEMR.create_form_submissions_as === "patient"
              ? styles.segmentButtonActive
              : null,
          ]}
          onPress={() =>
            setFourDEMR((prev) => ({
              ...prev,
              create_form_submissions_as: "patient",
            }))
          }
        >
          <Text style={styles.segmentButtonText}>Patient</Text>
        </Pressable>
      </View>

      <Text style={styles.label}>Document Type ID (optional)</Text>
      <TextInput
        style={styles.input}
        value={fourDEMR.document_type_id}
        onChangeText={(value) =>
          setFourDEMR((prev) => ({ ...prev, document_type_id: value }))
        }
        keyboardType="numeric"
      />

      <Text style={styles.label}>Provider IDs (optional, comma-separated)</Text>
      <TextInput
        style={styles.input}
        value={fourDEMR.provider_ids_csv}
        onChangeText={(value) =>
          setFourDEMR((prev) => ({ ...prev, provider_ids_csv: value }))
        }
        autoCapitalize="none"
        placeholder="123, 456"
      />
    </View>
  );

  const renderMetaPixelConnectionStep = () => (
    <View style={styles.formSection}>
      <View>
        <Text style={styles.sectionTitle}>Connect Meta</Text>
        <Text style={styles.helperText}>
          Required fields are marked with an asterisk. Meta may also show your
          Dataset ID as a Pixel ID.
        </Text>
      </View>

      <View
        style={[
          styles.metaConnectionLayout,
          isMobile ? styles.metaConnectionLayoutMobile : null,
        ]}
      >
        <View style={styles.metaConnectionForm}>
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Meta Dataset ID *</Text>
            <TextInput
              accessibilityLabel="Meta Dataset ID"
              accessibilityHint="Enter the numeric ID from Meta Events Manager"
              style={[
                styles.input,
                metaTouched.pixel_id && metaPixelIdError
                  ? styles.inputError
                  : null,
              ]}
              value={metaPixel.pixel_id}
              onChangeText={(value) => {
                invalidateMetaVerification();
                setMetaPixel((prev) => ({
                  ...prev,
                  pixel_id: value.replace(/\D/g, "").slice(0, 32),
                }));
              }}
              onBlur={() =>
                setMetaTouched((prev) => ({ ...prev, pixel_id: true }))
              }
              autoCapitalize="none"
              keyboardType="numeric"
              placeholder="123456789012345"
              maxLength={32}
            />
            {metaTouched.pixel_id && metaPixelIdError ? (
              <Text
                accessibilityLiveRegion="polite"
                style={styles.fieldErrorText}
              >
                {metaPixelIdError}
              </Text>
            ) : (
              <Text style={styles.fieldHelperText}>
                Find this numeric ID at the top of your web data source in Meta
                Events Manager. Meta may label it Pixel ID.
              </Text>
            )}
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Conversions API access token *</Text>
            <View style={styles.secretInputRow}>
              <TextInput
                accessibilityLabel="Conversions API access token"
                accessibilityHint="Enter the token generated in Meta Events Manager"
                style={[
                  styles.input,
                  styles.secretInput,
                  metaTouched.access_token && metaAccessTokenError
                    ? styles.inputError
                    : null,
                ]}
                value={metaPixel.access_token}
                onChangeText={(value) => {
                  invalidateMetaVerification();
                  setMetaPixel((prev) => ({ ...prev, access_token: value }));
                }}
                onBlur={() =>
                  setMetaTouched((prev) => ({ ...prev, access_token: true }))
                }
                autoCapitalize="none"
                autoCorrect={false}
                secureTextEntry={!showMetaAccessToken}
                placeholder="Paste your access token"
              />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={
                  showMetaAccessToken
                    ? "Hide Conversions API access token"
                    : "Show Conversions API access token"
                }
                style={({ pressed }) => [
                  styles.secretVisibilityButton,
                  pressed ? styles.pressedControl : null,
                ]}
                onPress={() => setShowMetaAccessToken((value) => !value)}
              >
                <Text style={styles.secretVisibilityText}>
                  {showMetaAccessToken ? "Hide" : "Show"}
                </Text>
              </Pressable>
            </View>
            {metaTouched.access_token && metaAccessTokenError ? (
              <Text
                accessibilityLiveRegion="polite"
                style={styles.fieldErrorText}
              >
                {metaAccessTokenError}
              </Text>
            ) : (
              <Text style={styles.fieldHelperText}>
                ClinicOS encrypts this credential. It will not appear in the
                review step.
              </Text>
            )}
          </View>
        </View>

        <View style={styles.metaHelpCard}>
          <Text style={styles.metaCardTitle}>
            Find your Dataset ID and access token
          </Text>
          <Text style={styles.metaCardText}>
            1. In Meta Events Manager, connect a Web data source or open an
            existing one.
          </Text>
          <Text style={styles.metaCardText}>
            2. Select its dataset and copy the numeric ID shown at the top.
          </Text>
          <Text style={styles.metaCardText}>
            3. Open Settings, find Conversions API, choose manual setup, and
            generate an access token.
          </Text>
          <Text style={styles.metaPrivacyEmphasis}>
            ClinicOS handles the browser and server event setup after you
            connect. You do not need to add Meta code separately to the form.
          </Text>
          <Pressable
            accessibilityRole="link"
            style={({ pressed }) => [
              styles.metaHelpLink,
              pressed ? styles.pressedControl : null,
            ]}
            onPress={() => {
              void Linking.openURL(META_SETUP_GUIDE_URL);
            }}
          >
            <Text style={styles.metaHelpLinkText}>
              Open Meta web data source guide
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="link"
            style={({ pressed }) => [
              styles.metaHelpLink,
              pressed ? styles.pressedControl : null,
            ]}
            onPress={() => {
              void Linking.openURL(META_CONVERSIONS_API_OVERVIEW_URL);
            }}
          >
            <Text style={styles.metaHelpLinkText}>
              Learn about Conversions API
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );

  const renderStep2 = () => {
    if (!selectedIntegration) {
      return (
        <Text style={styles.helperText}>Choose an integration first.</Text>
      );
    }

    if (selectedIntegration.status === "coming_soon") {
      return (
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>
            {selectedIntegration.display_name}
          </Text>
          <Text style={styles.helperText}>
            {selectedIntegration.disabled_reason ?? "Coming soon."}
          </Text>
          <PrimaryButton
            text="Request Custom Integration"
            onPress={requestAccess}
          />
          {requestAccessStatus ? (
            <Text style={styles.statusInfo}>{requestAccessStatus}</Text>
          ) : null}
        </View>
      );
    }

    if (selectedIntegration.status === "existing_flow") {
      return (
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>
            {selectedIntegration.display_name}
          </Text>
          <Text style={styles.helperText}>
            This integration currently uses the existing integrations flow.
          </Text>
          <PrimaryButton
            text="Open Existing Flow"
            onPress={() => navigate("/integrations?all")}
          />
          <View style={styles.verticalSpace} />
          <PrimaryButton
            text="Request Custom Integration"
            onPress={requestAccess}
          />
          {requestAccessStatus ? (
            <Text style={styles.statusInfo}>{requestAccessStatus}</Text>
          ) : null}
        </View>
      );
    }

    if (isWebhook) return renderWebhookStep();
    if (isPipedrive) return renderPipedriveStep();
    if (isFourDEMR) return renderFourDEMRStep();
    if (isMetaPixel) return renderMetaPixelConnectionStep();

    return (
      <Text style={styles.helperText}>
        This integration type is not wired for V2 setup yet.
      </Text>
    );
  };

  const renderStep3 = () => {
    if (isMetaPixel) {
      return (
        <View style={styles.formSection}>
          <View>
            <Text style={styles.sectionTitle}>Events & Privacy</Text>
            <Text style={styles.helperText}>
              Meta tracking only starts after the visitor grants marketing
              consent.
            </Text>
          </View>

          <View style={styles.metaEventCard}>
            <View style={styles.metaEventHeadingRow}>
              <View style={styles.metaEventText}>
                <Text style={styles.metaCardTitle}>Lead</Text>
                <Text style={styles.metaCardText}>
                  Sent after a successful production form submission. Browser
                  and server events use the same event ID for deduplication.
                </Text>
              </View>
              <View style={styles.enabledPill}>
                <Text style={styles.enabledPillText}>Enabled</Text>
              </View>
            </View>
          </View>

          <Pressable
            aria-checked={metaPixel.track_page_view}
            accessibilityRole="switch"
            accessibilityState={{ checked: metaPixel.track_page_view }}
            accessibilityLabel="Track PageView events"
            accessibilityHint="Sends a browser PageView event after marketing consent"
            testID="meta-page-view-toggle"
            onPress={() => {
              invalidateMetaVerification();
              setMetaPixel((prev) => ({
                ...prev,
                track_page_view: !prev.track_page_view,
              }));
            }}
            style={({ pressed }) => [
              styles.metaToggleCard,
              pressed ? styles.pressedControl : null,
            ]}
          >
            <View style={styles.metaEventText}>
              <Text style={styles.metaCardTitle}>Track PageView events</Text>
              <Text style={styles.metaCardText}>
                PageView events expose additional browsing context. Leave this
                off unless your clinic has approved it during privacy review.
              </Text>
            </View>
            <View
              style={[
                styles.switchTrack,
                metaPixel.track_page_view ? styles.switchTrackActive : null,
              ]}
            >
              <View
                style={[
                  styles.switchThumb,
                  metaPixel.track_page_view ? styles.switchThumbActive : null,
                ]}
              />
            </View>
          </Pressable>

          <View style={styles.metaConsentCard}>
            <Text style={styles.metaCardTitle}>
              Consent handled in the form
            </Text>
            <Text style={styles.metaCardText}>
              ClinicOS asks visitors on the first screen when your website has
              not already shared a marketing-consent decision. Visitors can
              continue without tracking.
            </Text>
            <Text style={styles.metaCardText}>
              If your website already has a cookie banner, it can optionally
              pass that choice to the form so visitors are not asked twice.
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ expanded: showMetaConsentHandoff }}
              style={({ pressed }) => [
                styles.metaHelpLink,
                pressed ? styles.pressedControl : null,
              ]}
              onPress={() =>
                setShowMetaConsentHandoff((isExpanded) => !isExpanded)
              }
            >
              <Text style={styles.metaHelpLinkText}>
                {showMetaConsentHandoff
                  ? "Hide optional website handoff"
                  : "View optional website handoff"}
              </Text>
            </Pressable>
            {showMetaConsentHandoff ? (
              <View style={styles.metaConsentInstructions}>
                <Text style={styles.metaCardText}>
                  Give this snippet to your website developer. Run it after the
                  form iframe loads and again whenever the visitor changes the
                  marketing-cookie choice. Use true for allow and false for
                  decline.
                </Text>
                <View style={styles.metaCodeBlock}>
                  <Text selectable style={styles.metaCodeText}>
                    {META_CONSENT_HANDOFF_SNIPPET}
                  </Text>
                </View>
                <Pressable
                  accessibilityRole="link"
                  style={({ pressed }) => [
                    styles.metaHelpLink,
                    pressed ? styles.pressedControl : null,
                  ]}
                  onPress={() => navigate("/deploy-widget")}
                >
                  <Text style={styles.metaHelpLinkText}>
                    Open form deployment
                  </Text>
                </Pressable>
              </View>
            ) : null}
          </View>

          <View style={styles.metaDataCard}>
            <Text style={styles.metaCardTitle}>Data sent to Meta</Text>
            <Text style={styles.metaCardText}>
              Event name and time, event ID, website origin, browser
              identifiers, IP address, and user agent.
            </Text>
            <Text style={styles.metaPrivacyEmphasis}>
              Form answers, procedure selections, and patient record fields are
              not included.
            </Text>
          </View>
        </View>
      );
    }

    if (!isWebhook) {
      return (
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>Mapping</Text>
          <Text style={styles.helperText}>
            This integration does not require custom field mapping in V2.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.formSection}>
        <View style={styles.rowButtons}>
          <Pressable
            style={[
              styles.segmentButton,
              mappingMode === "guided" ? styles.segmentButtonActive : null,
            ]}
            onPress={() => setMappingMode("guided")}
          >
            <Text style={styles.segmentButtonText}>Guided</Text>
          </Pressable>
          <Pressable
            style={[
              styles.segmentButton,
              mappingMode === "json" ? styles.segmentButtonActive : null,
            ]}
            onPress={() => setMappingMode("json")}
          >
            <Text style={styles.segmentButtonText}>JSON</Text>
          </Pressable>
        </View>

        {mappingMode === "guided" ? (
          <GuidedMappingEditor
            mappings={webhook.mappings}
            onChange={(nextMappings) => {
              setWebhook((prev) => ({
                ...prev,
                mappings: ensureRequiredMappings(nextMappings),
              }));
            }}
            onOpenSourceCatalog={(mappingId) =>
              setActiveSourceMappingId(mappingId)
            }
            errorsByPath={errorsByPath}
          />
        ) : (
          <JsonMappingEditor
            value={webhook.json_mode_draft}
            onChange={(value) =>
              setWebhook((prev) => ({ ...prev, json_mode_draft: value }))
            }
            parseError={hasValidated ? jsonParseResult.parseError : null}
            onFormat={() => {
              const parsed = parseJsonDraftMappings(webhook.json_mode_draft);
              if (parsed.parseError || !parsed.mappings) {
                setSaveError(parsed.parseError ?? "Invalid JSON.");
                return;
              }
              setWebhook((prev) => ({
                ...prev,
                json_mode_draft: mappingsToJsonDraft(parsed.mappings ?? []),
              }));
              setSaveError(null);
            }}
            onResetToTemplate={() => {
              if (!webhook.template_id) {
                setWebhook((prev) => ({
                  ...prev,
                  json_mode_draft: mappingsToJsonDraft(prev.mappings),
                }));
                return;
              }
              selectTemplate(webhook.template_id);
            }}
          />
        )}

        <Text style={styles.label}>Sample Submission JSON (Input Data)</Text>
        <Text style={styles.helperText}>
          This JSON is the source sample. Mapping edits change the simulated
          payload preview, not this input block.
        </Text>
        <TextInput
          style={styles.largeInput}
          multiline
          value={webhook.sample_payload_json}
          onChangeText={(value) =>
            setWebhook((prev) => ({ ...prev, sample_payload_json: value }))
          }
          autoCapitalize="none"
        />

        {previewPayload && isWebhook ? (
          <PayloadPreview payload={previewPayload} />
        ) : null}
      </View>
    );
  };

  const renderStep4 = () => {
    if (isMetaPixel) {
      const metaVerificationValue: Record<
        Exclude<TMetaVerificationStatus, "idle">,
        string
      > = {
        checking: "Checking…",
        verified: "☑️ Confirmed",
        unconfirmed: "❓ Not confirmed",
      };
      const metaReviewRows = [
        { label: "Integration", value: "Meta Pixel" },
        {
          label: "Dataset ID",
          value: metaPixel.pixel_id.trim() || "Not provided",
        },
        { label: "Access token", value: "••••••••••••" },
        { label: "Lead event", value: "Enabled for opted-in visitors" },
        {
          label: "PageView",
          value: metaPixel.track_page_view ? "Enabled" : "Disabled",
        },
        { label: "Status", value: "Active" },
        ...(metaVerificationStatus === "idle"
          ? []
          : [
              {
                label: "Verification",
                value: metaVerificationValue[metaVerificationStatus],
              },
            ]),
      ];

      return (
        <View style={styles.formSection}>
          <View>
            <Text style={styles.sectionTitle}>Review Meta connection</Text>
            <Text style={styles.helperText}>
              Confirm the configuration before connecting this clinic to Meta.
            </Text>
          </View>

          <View style={styles.metaReviewCard}>
            {metaReviewRows.map((row) => (
              <View style={styles.metaReviewRow} key={row.label}>
                <Text style={styles.metaReviewLabel}>{row.label}</Text>
                <Text selectable style={styles.metaReviewValue}>
                  {row.label === "Verification" ? (
                    <Text
                      accessibilityLiveRegion="polite"
                      testID="meta-verification-status"
                    >
                      {row.value}
                    </Text>
                  ) : (
                    row.value
                  )}
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.badgesContainer}>
            {badges.map((badge) => (
              <View
                key={badge.label}
                style={[
                  styles.badge,
                  badge.status === "ok"
                    ? styles.badgeOk
                    : badge.status === "error"
                      ? styles.badgeError
                      : styles.badgeIdle,
                ]}
              >
                <Text style={styles.badgeTitle}>{badge.label}</Text>
                {badge.detail ? (
                  <Text style={styles.badgeDetail}>{badge.detail}</Text>
                ) : null}
              </View>
            ))}
          </View>

          <SecondaryButton
            text={isValidating ? "Validating..." : "Validate Configuration"}
            onPress={validateAndSimulate}
            disabled={
              isValidating ||
              isSaving ||
              Boolean(metaPixelIdError) ||
              Boolean(metaAccessTokenError)
            }
            loading={isValidating}
            fullWidth
          />

          {saveError ? (
            <Text accessibilityLiveRegion="polite" style={styles.errorText}>
              {saveError}
            </Text>
          ) : null}
          {saveSuccess ? (
            <View style={styles.metaSuccessCard}>
              <Text
                accessibilityLiveRegion="polite"
                style={styles.metaSuccessTitle}
              >
                Meta connected
              </Text>
              <Text style={styles.metaCardText}>
                Consented production form submissions can now send deduplicated
                Lead events through Meta Pixel and Conversions API.
              </Text>
            </View>
          ) : null}

          <PrimaryButton
            text={isSaving ? "Connecting..." : "Connect Meta"}
            onPress={handleSave}
            disabled={
              isSaving ||
              isValidating ||
              selectedIntegration?.status !== "ready" ||
              Boolean(metaPixelIdError) ||
              Boolean(metaAccessTokenError)
            }
            loading={isSaving}
            fullWidth
          />
        </View>
      );
    }

    const payloadPreview = payloadDraft.payload
      ? JSON.stringify(payloadDraft.payload, null, 2)
      : payloadDraft.errors.join("\n");

    return (
      <View style={styles.formSection}>
        <Text style={styles.sectionTitle}>Validation</Text>
        <View style={styles.badgesContainer}>
          {badges.map((badge) => (
            <View
              key={badge.label}
              style={[
                styles.badge,
                badge.status === "ok"
                  ? styles.badgeOk
                  : badge.status === "error"
                    ? styles.badgeError
                    : styles.badgeIdle,
              ]}
            >
              <Text style={styles.badgeTitle}>{badge.label}</Text>
              {badge.detail ? (
                <Text style={styles.badgeDetail}>{badge.detail}</Text>
              ) : null}
            </View>
          ))}
        </View>

        <View style={styles.actionRow}>
          <SecondaryButton
            text="Validate & Simulate Payload"
            onPress={validateAndSimulate}
            fullWidth
          />
        </View>

        {previewPayload && isWebhook ? (
          <PayloadPreview payload={previewPayload} />
        ) : null}

        <Text style={styles.label}>Create Payload Draft</Text>
        <View style={styles.payloadBox}>
          <Text selectable style={styles.payloadText}>
            {payloadPreview}
          </Text>
        </View>

        {saveError ? <Text style={styles.errorText}>{saveError}</Text> : null}
        {saveSuccess ? (
          <Text style={styles.successText}>{saveSuccess}</Text>
        ) : null}

        <PrimaryButton
          text={isSaving ? "Saving..." : "Save Integration"}
          onPress={handleSave}
          disabled={isSaving || selectedIntegration?.status !== "ready"}
          loading={isSaving}
          fullWidth
        />
      </View>
    );
  };

  const renderCurrentStep = () => {
    if (stepIndex === 0) return renderStep1();
    if (stepIndex === 1) return renderStep2();
    if (stepIndex === 2) return renderStep3();
    return renderStep4();
  };

  if (catalogLoading) {
    return (
      <View style={styles.centerState}>
        <ActivityIndicator size="large" color={TOKENS.color.focus} />
        <Text style={styles.helperText}>Loading integration catalog...</Text>
      </View>
    );
  }

  if (catalogError) {
    return (
      <View style={styles.centerState}>
        <Text style={styles.errorText}>{catalogError}</Text>
        <PrimaryButton
          text="Retry"
          onPress={() => {
            setCatalogLoading(true);
            setCatalogError(null);
            setStepIndex(0);
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container} testID="integration-setup-wizard">
      <View style={styles.stepperContainer}>
        {stepTitles.map((stepTitle, index) => {
          const isActive = index === stepIndex;
          const isCompleted = index < stepIndex;

          return (
            <Pressable
              key={stepTitle}
              onPress={() => {
                if (index <= stepIndex) setStepIndex(index);
              }}
              style={[
                styles.stepPill,
                isActive ? styles.stepPillActive : null,
                isCompleted ? styles.stepPillCompleted : null,
              ]}
            >
              <Text style={styles.stepIndex}>{index + 1}</Text>
              <Text style={styles.stepText}>{stepTitle}</Text>
            </Pressable>
          );
        })}
      </View>

      <ScrollView
        key={stepIndex}
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {renderCurrentStep()}
      </ScrollView>

      <View style={styles.footerActions}>
        <SecondaryButton
          text="Back"
          onPress={goBack}
          disabled={stepIndex === 0}
        />
        {stepIndex < stepTitles.length - 1 ? (
          <PrimaryButton
            text="Next"
            onPress={goNext}
            disabled={!canAdvanceFromCurrentStep}
          />
        ) : null}
      </View>

      <SourcePathCatalogDrawer
        visible={Boolean(activeSourceMappingId)}
        sourcePaths={sourcePaths}
        onClose={() => setActiveSourceMappingId(null)}
        onInsert={(path) => {
          if (!activeSourceMappingId) return;
          setWebhook((prev) => ({
            ...prev,
            mappings: prev.mappings.map((row) =>
              row.id === activeSourceMappingId
                ? { ...row, source_path: path }
                : row,
            ),
          }));
          setActiveSourceMappingId(null);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    minHeight: 640,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.md,
  },
  centerState: {
    minHeight: 240,
    alignItems: "center",
    justifyContent: "center",
    gap: TOKENS.spacing.sm,
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
  },
  stepperContainer: {
    gap: TOKENS.spacing.xs,
  },
  stepPill: {
    minHeight: 44,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    paddingHorizontal: TOKENS.spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.xs,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  stepPillActive: {
    borderColor: TOKENS.color.focus,
    backgroundColor: TOKENS.color.infoBg,
  },
  stepPillCompleted: {
    backgroundColor: TOKENS.color.successBg,
    borderColor: TOKENS.color.success,
  },
  stepIndex: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    width: 18,
  },
  stepText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: TOKENS.spacing.md,
  },
  formSection: {
    gap: TOKENS.spacing.sm,
  },
  sectionTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.xl,
    lineHeight: TOKENS.lineHeight.xl,
    color: TOKENS.color.textPrimary,
  },
  helperText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.textSecondary,
  },
  statusInfo: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.infoText,
    fontSize: TOKENS.fontSize.sm,
  },
  label: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  input: {
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    minHeight: 44,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.xs,
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    backgroundColor: TOKENS.color.surface,
  },
  inputError: {
    borderColor: TOKENS.color.error,
    borderWidth: 2,
  },
  fieldGroup: {
    gap: TOKENS.spacing.xs,
  },
  fieldHelperText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.textMuted,
  },
  fieldErrorText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.error,
  },
  metaConnectionLayout: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: TOKENS.spacing.lg,
  },
  metaConnectionLayoutMobile: {
    flexDirection: "column",
  },
  metaConnectionForm: {
    flex: 2,
    width: "100%",
    gap: TOKENS.spacing.md,
  },
  metaHelpCard: {
    flex: 1,
    width: "100%",
    gap: TOKENS.spacing.xs,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.infoBg,
  },
  metaCardTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.md,
    lineHeight: TOKENS.lineHeight.md,
    color: TOKENS.color.textPrimary,
  },
  metaCardText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.textSecondary,
  },
  metaHelpLink: {
    alignSelf: "flex-start",
    minHeight: TOKENS.spacing.xxxl + TOKENS.spacing.xxs,
    justifyContent: "center",
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  metaHelpLinkText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.infoText,
    textDecorationLine: "underline",
  },
  metaConsentInstructions: {
    gap: TOKENS.spacing.sm,
  },
  metaCodeBlock: {
    backgroundColor: TOKENS.color.codeBg,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    padding: TOKENS.spacing.md,
  },
  metaCodeText: {
    color: TOKENS.color.codeText,
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  secretInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.xs,
  },
  secretInput: {
    flex: 1,
  },
  secretVisibilityButton: {
    minHeight: TOKENS.spacing.xxxl + TOKENS.spacing.xxs,
    minWidth: TOKENS.spacing.xxxl + TOKENS.spacing.xl,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: TOKENS.color.borderStrong,
    borderRadius: TOKENS.radius.md,
    backgroundColor: TOKENS.color.surface,
    paddingHorizontal: TOKENS.spacing.sm,
  },
  secretVisibilityText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  pressedControl: {
    backgroundColor: TOKENS.color.infoBg,
  },
  metaEventCard: {
    borderWidth: 1,
    borderColor: TOKENS.color.success,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.successBg,
  },
  metaEventHeadingRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: TOKENS.spacing.md,
  },
  metaEventText: {
    flex: 1,
    gap: TOKENS.spacing.xxs,
  },
  enabledPill: {
    borderRadius: TOKENS.radius.pill,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.xxs,
    backgroundColor: TOKENS.color.success,
  },
  enabledPillText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.surface,
  },
  metaToggleCard: {
    minHeight: TOKENS.spacing.xxxl + TOKENS.spacing.xxl,
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.md,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.surface,
  },
  switchTrack: {
    width: TOKENS.spacing.xxxl,
    height: TOKENS.spacing.xl,
    borderRadius: TOKENS.radius.pill,
    padding: TOKENS.spacing.xxs,
    justifyContent: "center",
    backgroundColor: TOKENS.color.disabledBg,
  },
  switchTrackActive: {
    backgroundColor: TOKENS.color.focus,
  },
  switchThumb: {
    width: TOKENS.spacing.md,
    height: TOKENS.spacing.md,
    borderRadius: TOKENS.radius.pill,
    backgroundColor: TOKENS.color.surface,
  },
  switchThumbActive: {
    transform: [{ translateX: TOKENS.spacing.md }],
  },
  metaConsentCard: {
    gap: TOKENS.spacing.xs,
    borderWidth: 1,
    borderColor: TOKENS.color.warning,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.warningBg,
  },
  metaDataCard: {
    gap: TOKENS.spacing.xs,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  metaPrivacyEmphasis: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.textPrimary,
  },
  metaReviewCard: {
    gap: TOKENS.spacing.xs,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.sm,
    backgroundColor: TOKENS.color.surface,
  },
  metaReviewRow: {
    minHeight: TOKENS.spacing.xxxl + TOKENS.spacing.xxs,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: TOKENS.spacing.xs,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.xs,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  metaReviewLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  metaReviewValue: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    textAlign: "right",
  },
  metaSuccessCard: {
    gap: TOKENS.spacing.xs,
    borderWidth: 1,
    borderColor: TOKENS.color.success,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    backgroundColor: TOKENS.color.successBg,
  },
  metaSuccessTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.md,
    lineHeight: TOKENS.lineHeight.md,
    color: TOKENS.color.success,
  },
  largeInput: {
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    minHeight: 180,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.sm,
    fontFamily: TOKENS.font.mono,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textPrimary,
    textAlignVertical: "top",
    backgroundColor: TOKENS.color.surface,
  },
  disabledInput: {
    backgroundColor: TOKENS.color.surfaceMuted,
    color: TOKENS.color.textMuted,
  },
  inlineInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.xs,
  },
  inlineInput: {
    flex: 1,
  },
  smallActionButton: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: TOKENS.color.borderStrong,
    borderRadius: TOKENS.radius.md,
    justifyContent: "center",
    paddingHorizontal: TOKENS.spacing.xs,
  },
  smallActionText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  rowButtons: {
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
    flexWrap: "wrap",
  },
  segmentButton: {
    minHeight: 44,
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    paddingHorizontal: TOKENS.spacing.md,
    justifyContent: "center",
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  segmentButtonActive: {
    borderColor: TOKENS.color.focus,
    backgroundColor: TOKENS.color.infoBg,
  },
  segmentButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  templateContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.xs,
  },
  templateCard: {
    flexGrow: 1,
    flexBasis: 180,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    padding: TOKENS.spacing.sm,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  templateCardActive: {
    borderColor: TOKENS.color.focus,
    backgroundColor: TOKENS.color.infoBg,
  },
  templateTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
    marginBottom: TOKENS.spacing.xxs,
  },
  templateDescription: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
    lineHeight: TOKENS.lineHeight.xs,
  },
  badgesContainer: {
    gap: TOKENS.spacing.xs,
  },
  badge: {
    borderRadius: TOKENS.radius.md,
    borderWidth: 1,
    padding: TOKENS.spacing.sm,
  },
  badgeOk: {
    borderColor: TOKENS.color.success,
    backgroundColor: TOKENS.color.successBg,
  },
  badgeError: {
    borderColor: TOKENS.color.error,
    backgroundColor: TOKENS.color.errorBg,
  },
  badgeIdle: {
    borderColor: TOKENS.color.border,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  badgeTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  badgeDetail: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  actionRow: {
    marginTop: TOKENS.spacing.xs,
  },
  payloadBox: {
    borderWidth: 1,
    borderColor: TOKENS.color.borderStrong,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.sm,
    backgroundColor: TOKENS.color.codeBg,
  },
  payloadText: {
    fontFamily: TOKENS.font.mono,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.codeText,
  },
  errorText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.error,
  },
  successText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.success,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: TOKENS.spacing.sm,
    marginTop: TOKENS.spacing.xs,
  },
  verticalSpace: {
    height: TOKENS.spacing.xs,
  },
});

export default IntegrationWizard;
